import type { ImportFieldGap } from '@repo/types'

/**
 * One candidate product in the picker. Every row the AE has seen stays in this
 * array — `selected` is what decides whether it is issued, NOT membership. That
 * is the difference between this and the field array it replaced: unticking a
 * scraped product has to leave it on screen so it can be ticked back on, which
 * a remove-from-array model cannot do.
 */
export interface ProductFormRow {
  title: string
  sku: string
  description: string
  images: string[]
  /** Where the seeder read this from (VRG-308 AC7 provenance). Sent on issue. */
  sourceUrl: string
  /**
   * Where to send the AE to look at the real thing. Empty when we have nothing
   * honest to point at, and the open-in-new-tab control is then hidden rather
   * than shown pointing somewhere useless (§46: record state hides).
   */
  productUrl: string
  /**
   * How `productUrl` was arrived at, because the two deserve different wording.
   * `page` is the actual listing. `search` is the store's own search for this
   * title — used on the Shopify catalog path, where the proposal carries the
   * catalog endpoint rather than each product's permalink.
   */
  productUrlKind: 'none' | 'page' | 'search'
  /** Issued only when true. Unticking excludes without discarding the row. */
  selected: boolean
  /** Typed by hand rather than scraped: no source to restore, so it gets an X. */
  manual: boolean
  /**
   * VRG-723 — the import field report: what the import could NOT pull in for this product, and why
   * (from the API). Absent on hand-typed rows. A gap the AE has since filled is hidden on the card
   * (`openFieldGaps`), never deleted, so the reason comes back if they clear the field again.
   */
  missing?: ImportFieldGap[]
}

/** VRG-723 — the report's gaps that are still open on the row as the AE has edited it. */
export const openFieldGaps = (row: Pick<ProductFormRow, 'missing' | 'images' | 'sku' | 'description'>): ImportFieldGap[] =>
  (row.missing ?? []).filter((gap) => {
    // An og:image note is a caveat about a photo that IS there; keep it while that photo is.
    if (gap.field === 'images') return !row.images?.length || /og:image/.test(gap.reason)
    // Shopify's made-up `SHOPIFY-<id>` is a placeholder until the AE types a real SKU over it.
    if (gap.field === 'sku') return !row.sku?.trim() || /^SHOPIFY-\d+$/.test(row.sku.trim())
    if (gap.field === 'description') return !row.description?.trim()
    return true
  })

/**
 * The ONLY thing `ProductPicker` needs from its host form.
 *
 * Kept minimal on purpose (VRG-369). The picker was built inside `CreateDemoModal`
 * and typed against that modal's whole `ReviewFormValues` — brand name, tier,
 * colours, fonts, lifetime — none of which it reads. A second consumer (the Demos
 * console's Add-products modal) has none of those fields, so the picker's contract
 * is narrowed to the one field it actually controls. Callers whose form is wider
 * cast their `Control` to this at the boundary; RHF's `Control<T>` is invariant,
 * so a structural subtype is not assignable and there is no cast-free spelling.
 */
export interface ProductPickerFormValues {
  products: ProductFormRow[]
}

export const EMPTY_PRODUCT_ROW: ProductFormRow = {
  title: '',
  sku: '',
  description: '',
  images: [],
  sourceUrl: '',
  productUrl: '',
  productUrlKind: 'none',
  selected: true,
  manual: true,
}
